import { ReceiveItem } from 'src/features/transfers/TransfersTypes'
import * as S from './ConfirmAddressAndBarcode.styles'
import TextLabel from '../TextLabel/TextLabel'
import { InputAdornment, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import ScanCodeIcon from 'src/assets/images/other-images/ScanCode.svg'
import { useDispatch } from 'react-redux'
import { actions } from '../../../TransfersState'
import { ReceiveItemPageModes } from 'src/features/transfers/pages/TransferReceive/ReceiveItem/ReceiveItem'
import { toast } from 'react-toastify'
import Modal from 'src/components/Modal'

interface ConfirmAddressAndBarcodeProps {
  badge: string
  idCage: number
  cageAddress: string
  item: ReceiveItem
  setStartedAt: React.Dispatch<React.SetStateAction<Date | null>>
  setPageMode: React.Dispatch<React.SetStateAction<ReceiveItemPageModes>>
}

interface FormData {
  address: string
  barcode: string
  supervisorBadge: string
}

const ConfirmAddressAndBarcode = ({
  badge,
  idCage,
  cageAddress,
  item,
  setStartedAt,
  setPageMode,
}: ConfirmAddressAndBarcodeProps) => {
  const {
    register,
    getValues,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
    setFocus,
  } = useForm<FormData>()

  const dispatch = useDispatch()

  const [addressConfirmed, setAddressConfirmed] = useState(false)
  const [preventDoubleRequest, setPreventDoubleRequest] = useState(false)

  const [showModalProductNotFound, setShowModalProductNotFound] = useState(false)

  useEffect(() => {
    setAddressConfirmed(false)
    setPreventDoubleRequest(false)
  }, [item])

  const data = useMemo(() => {
    if (item.isGondola) {
      return {
        text: 'DIRETO GÔNDOLA',
        inputText: 'Bipe o endereço da gôndola',
        addressTextColor: '#fff',
        addressBackgroundColor: '#000',
      }
    }
    return {
      text: 'REPOSIÇÃO DE EXCEDENTE',
      inputText: 'Bipe o endereço de excedente',
      addressTextColor: '#000',
      addressBackgroundColor: '#fff',
    }
  }, [item])

  const dateNow = () => {
    const date = new Date()
    date.setHours(date.getHours() - 3)
    return date
  }

  const onSubmitAddress = () => {
    clearErrors()

    const address = getValues('address')

    if (!address) {
      setError('address', {
        message: 'Campo obrigatório',
      })
      return
    }

    if (item.bestPosition.address.toUpperCase() !== address.toUpperCase()) {
      setError('address', {
        message: 'Endereço inválido',
      })
      return
    }

    setStartedAt(dateNow())
    setAddressConfirmed(true)
  }

  const onSubmitBarcode = () => {
    if (preventDoubleRequest) {
      return
    }

    setPreventDoubleRequest(true)

    clearErrors()

    const barcode = getValues('barcode')

    if (!barcode) {
      setError('barcode', {
        message: 'Campo obrigatório',
      })
      setPreventDoubleRequest(false)
      return
    }

    const onSuccess = () => {
      setPreventDoubleRequest(false)
      setPageMode(ReceiveItemPageModes.ITEM_COUNT)
    }

    const onError = (error: string) => {
      setPreventDoubleRequest(false)
      setError('barcode', { message: error })
    }

    dispatch(actions.validateProductBarcode({
      badge,
      idModel: item.idModel,
      barcode,
      onSuccess,
      onError,
    }))
  }

  const onSubmitProductNotFound = () => {
    if (preventDoubleRequest) {
      return
    }

    setPreventDoubleRequest(true)

    clearErrors()

    const supervisorBadge = getValues('supervisorBadge')

    if (!supervisorBadge) {
      setError('supervisorBadge', {
        message: 'Campo obrigatório',
      })
      setPreventDoubleRequest(false)
      return
    }

    const onSuccess = () => {
      setShowModalProductNotFound(false)
      setPreventDoubleRequest(false)
      toast.info('Produto registrado como não encontrado')
      setValue('address', '')
      setValue('barcode', '')
      setValue('supervisorBadge', '')
      setFocus('address')
    }

    const onError = () => {
      setPreventDoubleRequest(false)
      setValue('supervisorBadge', '')
    }

    dispatch(actions.receiveSupervisorProductNotFound({
      badge: supervisorBadge,
      idCage,
      item,
      onSuccess,
      onError,
    }))
  }

  const handleOnKeyDown = (event: React.KeyboardEvent, onSubmit: () => void) => {
    const enterKeyCode = 13
    const tabKeyCode = 9

    if ([enterKeyCode, tabKeyCode].includes(event.keyCode) && !event.shiftKey) {
      event.preventDefault()
      onSubmit()
    }
  }

  return (
    <>
      <S.Form>
        <S.ContainerBox>
          <TextLabel width='100%' backgroundColor='#c4c4c4'>
            <Typography>{cageAddress}</Typography>
          </TextLabel>
        </S.ContainerBox>
        <S.Container>
          <S.ContainerInfo>
            <S.ContainerType style={{ backgroundColor: data.addressBackgroundColor }}>
              <Typography color={data.addressTextColor} fontWeight='bold'>{data.text}</Typography>
              <S.ContainerBox>
                <TextLabel width='100%' backgroundColor='#FBEF3C'>
                  <Typography fontWeight='bold'>{item.bestPosition.address}</Typography>
                </TextLabel>
              </S.ContainerBox>
            </S.ContainerType>
          </S.ContainerInfo>
          <S.ContainerInput>
            <S.CustomTextField
              onKeyDown={(e) => handleOnKeyDown(e, onSubmitAddress)}
              name='address'
              id='address'
              label={data.inputText}
              type='text'
              autoFocus
              fullWidth
              error={!!errors?.address}
              helperText={errors?.address?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position='end'
                    sx={{ cursor: 'pointer' }}
                    data-testid='input-adornment-test-id'
                  >
                    <img src={ScanCodeIcon} alt='scan code icon' data-testid='scan-code-icon-test-id' />
                  </InputAdornment>
                ),
                inputProps: {
                  ...register('address'),
                  'data-testid': 'address-test-id',
                },
              }}
              disabled={addressConfirmed}
            />
          </S.ContainerInput>
          {addressConfirmed && (
          <>
            <S.ContainerBox style={{ marginTop: '.6rem' }}>
              <TextLabel width='100%' height='3.5rem' backgroundColor='#7feb77'>
                <Typography fontSize='14px' fontWeight='bold'>{item.productDescription}</Typography>
              </TextLabel>
            </S.ContainerBox>
            <S.ContainerInput style={{ marginTop: '.3rem' }}>
              <S.CustomTextField
                onKeyDown={(e) => handleOnKeyDown(e, onSubmitBarcode)}
                name='barcode'
                id='barcode'
                label='Bipe o código de barras do produto'
                type='text'
                autoFocus
                fullWidth
                error={!!errors?.barcode}
                helperText={errors?.barcode?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position='end'
                      sx={{ cursor: 'pointer' }}
                      data-testid='input-adornment-test-id'
                    >
                      <img src={ScanCodeIcon} alt='scan code icon' data-testid='scan-code-icon-test-id' />
                    </InputAdornment>
                  ),
                  inputProps: {
                    ...register('barcode'),
                    'data-testid': 'barcode-test-id',
                  },
                }}
              />
            </S.ContainerInput>
            <S.ContainerButton>
              <S.CustomButton
                title='Produto não encontrado'
                bgColor='cancel'
                hoverColor='cancelHover'
                letterColor='white'
                data-testid='button-product-not-found-test-id'
                onClick={() => setShowModalProductNotFound(true)}
              />
            </S.ContainerButton>
          </>
          )}
        </S.Container>
      </S.Form>
      <Modal
        show={showModalProductNotFound}
        testId='modal-product-not-found-test-id'
        centered
        title='Produto não encontrado'
        closeButtonAction={() => setShowModalProductNotFound(false)}
        closedButton
        body={(
          <S.ModalProductNotFoundContainer>
            <S.ModalProductNotFoundText>
              Produto <span style={{ fontWeight: 'bold' }}>{item.productDescription}</span> marcado como <span style={{ color: 'red', fontWeight: 'bold' }}>não encontrado</span> na gaiola de transferência <span style={{ fontWeight: 'bold' }}>{cageAddress}</span>.
            </S.ModalProductNotFoundText>
            <S.ModalProductNotFoundText style={{ fontWeight: 'bold', marginTop: '.6rem' }}>
              Chame o fiscal para confirmar a operação!
            </S.ModalProductNotFoundText>
            <S.CustomTextField
              sx={{
                marginTop: '-.1rem',
              }}
              inputProps={{
                ...register('supervisorBadge'),
                'data-testid': 'input-supervisor-badge-test-id',
              }}
              error={!!errors.supervisorBadge}
              helperText={errors.supervisorBadge?.message}
              label='Crachá do fiscal'
              name='supervisor-badge'
              testId='input-supervisor-badge-test-id'
              type='password'
              size='small'
              fullWidth
              autoComplete='off'
              autoFocus
              onKeyDown={(e) => handleOnKeyDown(e, onSubmitProductNotFound)}
            />
          </S.ModalProductNotFoundContainer>
        )}
      />
    </>
  )
}

export default ConfirmAddressAndBarcode
